import { Box, Chip, Stack, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CorporateFareOutlinedIcon from "@mui/icons-material/CorporateFareOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { Link, useNavigate } from "react-router-dom";
import { nextStep, ONBOARDING_BASE_PATH } from "../registry";
import { useFlowState, useFlowActions } from "../../state/FlowProvider";
import { onboardingTokens } from "../../theme";
import { t } from "../../../../locales/t";

/**
 * The front door: which kind of member is signing up.
 *
 * Corporate is rendered in full and cannot be activated. Hiding it would make
 * the product look like it has no answer for organizations; letting it fail on
 * click would look broken. Shown, labelled unavailable, and inert is the only
 * option that reads as deliberate. ECH-321 builds the pathway itself.
 *
 * There is no mentor or mentee choice anywhere here. Goals replace roles — a
 * member who wants to mentor says so by choosing that goal later.
 */

const PathwayCard = ({
  label,
  description,
  cta,
  icon,
  disabled,
  disabledLabel,
  hint,
  onSelect,
}) => (
  <Box
    component="button"
    type="button"
    disabled={disabled}
    onClick={onSelect}
    aria-describedby={hint ? `${label}-hint` : undefined}
    data-testid="pathway-card"
    sx={{
      flex: 1,
      // Nested column flex, so the two cards agree on where each band of
      // content starts and ends however long the copy is. The rows below are
      // laid out by flex rules rather than by fixed heights, which would only
      // hold at the width they were measured on.
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      textAlign: "left",
      p: 3,
      borderRadius: "1rem",
      backgroundColor: onboardingTokens.card,
      border: `1.5px solid ${onboardingTokens.track}`,
      cursor: disabled ? "default" : "pointer",
      // Not greyed into illegibility: it should read as "not yet", not "broken".
      opacity: disabled ? 0.72 : 1,
      transition: "border-color 0.2s, box-shadow 0.2s",
      "&:hover": disabled
        ? {}
        : {
            borderColor: onboardingTokens.teal,
            boxShadow: `0 0 0 3px rgba(1, 190, 204, 0.14)`,
          },
      "&:focus-visible": {
        outline: `2px solid ${onboardingTokens.teal}`,
        outlineOffset: 2,
      },
    }}
  >
    {/* Fixed size and no shrinking, so the icon cannot be squeezed by a longer
        description and drag the text below it out of line. */}
    <Box
      sx={{
        mb: 2,
        width: 44,
        height: 44,
        flexShrink: 0,
        borderRadius: "0.75rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: onboardingTokens.lightTeal,
        color: onboardingTokens.navy,
      }}
    >
      {icon}
    </Box>

    {/* The chip is taller than the text beside it, so without a floor here the
        labelled card's title row is a couple of pixels deeper than the other
        and everything below inherits the offset. */}
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      sx={{ mb: 0.5, minHeight: 28, flexShrink: 0 }}
      data-testid="pathway-label"
    >
      <Typography sx={{ fontWeight: 700, fontSize: "0.9375rem" }}>
        {label}
      </Typography>
      {disabled && (
        <Chip
          size="small"
          label={disabledLabel}
          sx={{
            height: 20,
            fontSize: "0.6875rem",
            fontWeight: 600,
            color: onboardingTokens.mutedForeground,
            backgroundColor: onboardingTokens.muted,
          }}
        />
      )}
    </Stack>

    {/* Absorbs the difference in copy length. Whichever description is shorter
        gains the slack, so both footers land on the same line rather than one
        floating up. */}
    <Typography
      sx={{
        flexGrow: 1,
        fontSize: "0.8125rem",
        color: onboardingTokens.mutedForeground,
        lineHeight: 1.55,
      }}
    >
      {description}
    </Typography>

    {/* Both branches are the same shape — one row, same top margin, no
        shrinking — so the footer sits at one height whether the card is
        actionable or explaining why it is not. */}
    <Box sx={{ mt: 2, flexShrink: 0 }} data-testid="pathway-footer">
      {disabled ? (
        <Typography
          id={`${label}-hint`}
          sx={{
            fontSize: "0.8125rem",
            color: onboardingTokens.mutedForeground,
            lineHeight: 1.55,
          }}
        >
          {hint}
        </Typography>
      ) : (
        <Stack
          direction="row"
          alignItems="center"
          gap={0.5}
          sx={{ color: onboardingTokens.teal }}
        >
          <Typography
            sx={{ fontSize: "0.8125rem", fontWeight: 600, lineHeight: 1.55 }}
          >
            {cta}
          </Typography>
          <ArrowForwardIcon sx={{ fontSize: "1rem" }} />
        </Stack>
      )}
    </Box>
  </Box>
);

const WelcomeStep = ({ step }) => {
  const state = useFlowState();
  const { setPathway } = useFlowActions();
  const navigate = useNavigate();

  const choosePerson = () => {
    setPathway("person");

    // Routed against the state this choice produces, not the state in hand.
    // Dispatch is asynchronous, so asking the registry now would ask it about a
    // flow with no pathway — and the next reachable step would be wrong.
    const next = nextStep(step.id, { ...state, pathway: "person" });
    if (next) navigate(`${ONBOARDING_BASE_PATH}/${next.path}`);
  };

  return (
    <Box sx={{ textAlign: "center" }}>
      <Typography
        variant="h1"
        sx={{
          fontSize: "clamp(1.625rem, 4.5vw, 2.5rem)",
          lineHeight: 1.18,
          letterSpacing: "-0.01em",
          mb: 1.75,
        }}
      >
        {t("onboarding.welcome.headline")}
      </Typography>

      <Typography
        sx={{
          fontWeight: 700,
          fontStyle: "italic",
          fontSize: "clamp(1rem, 2.5vw, 1.125rem)",
          color: onboardingTokens.teal,
          lineHeight: 1.5,
          mb: 1.25,
        }}
      >
        {t("onboarding.welcome.tagline")}
      </Typography>

      <Typography
        sx={{
          fontSize: "0.9375rem",
          color: onboardingTokens.mutedForeground,
          lineHeight: 1.65,
          mb: 4,
        }}
      >
        {t("onboarding.welcome.subtitle")}
      </Typography>

      <Typography
        component="h2"
        sx={{
          fontSize: "0.75rem",
          color: onboardingTokens.mutedForeground,
          textAlign: "left",
          mb: 1.5,
        }}
      >
        {t("onboarding.welcome.joiningAs")}
      </Typography>

      {/* `stretch` is the flex default, but stated because the whole alignment
          depends on it: the cards must be equal height for their inner rows to
          line up. */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        gap={1.75}
        alignItems="stretch"
      >
        <PathwayCard
          label={t("onboarding.welcome.person.label")}
          description={t("onboarding.welcome.person.description")}
          cta={t("onboarding.welcome.person.cta")}
          icon={<PersonOutlineIcon sx={{ fontSize: 28 }} />}
          onSelect={choosePerson}
        />
        <PathwayCard
          label={t("onboarding.welcome.organization.label")}
          description={t("onboarding.welcome.organization.description")}
          icon={<CorporateFareOutlinedIcon sx={{ fontSize: 28 }} />}
          disabled
          disabledLabel={t("onboarding.welcome.organization.unavailable")}
          hint={t("onboarding.welcome.organization.unavailableHint")}
        />
      </Stack>

      <Typography
        sx={{
          fontSize: "0.8125rem",
          color: onboardingTokens.mutedForeground,
          mt: 3.5,
        }}
      >
        {t("onboarding.welcome.haveAccount")}{" "}
        {/* Router link, not a bare anchor. An `href` here tears down the app
            and reloads every asset to move between two client-side routes,
            which is slow and loses any flow state held in memory. */}
        <Box
          component={Link}
          to="/login"
          sx={{ color: onboardingTokens.teal, fontWeight: 600 }}
        >
          {t("onboarding.welcome.signIn")}
        </Box>
      </Typography>
    </Box>
  );
};

export default WelcomeStep;
